import type { JSX, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { addToast, Button, Card, CardBody, CardHeader, Chip, Spinner } from '@heroui/react';

import {
  useAcceptInvitationDirect,
  useDeclineInvitation,
  useInvitations,
} from '~/hooks/useInvitations';
import { logger } from '~/utils/logger';

import { Feedback } from '~/components/Feedback';

/**
 * Settings page for managing pending organization invitations.
 *
 * Shows all pending invitations with details and actions.
 * Users can accept or decline invitations from this page.
 */
export function InvitationsSettings(): JSX.Element {
  const { data: invitations, isLoading } = useInvitations();
  const acceptMutation = useAcceptInvitationDirect();
  const declineMutation = useDeclineInvitation();
  const navigate = useNavigate();

  const handleAccept = async (invitationId: string, orgName: string): Promise<void> => {
    logger.info({ component: 'InvitationsSettings' }, 'Accepting invitation directly', {
      invitationId,
      orgName,
    });

    try {
      const response = await acceptMutation.mutateAsync(invitationId);
      logger.info({ component: 'InvitationsSettings' }, 'Invitation accepted successfully');

      // Show prominent success toast
      addToast({
        description: `Welcome to ${orgName}! You are now a member.`,
        color: 'success',
      });

      // Navigate to new organization's projects page (TenantContext will switch orgs)
      void navigate(`/projects?org=${response.organization_id}`, { replace: true });
    } catch (error: unknown) {
      // Check if this is an email not verified error (403)
      const isEmailNotVerified =
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string' &&
        error.message.includes('Email not verified');

      if (isEmailNotVerified) {
        logger.warn(
          { component: 'InvitationsSettings' },
          'Direct acceptance blocked: email not verified',
          { invitationId, orgName },
        );
        addToast({
          description:
            'Email verification required. Please use the link in your email or sign in with Google/GitHub.',
          color: 'danger',
        });
      } else {
        logger.error(
          { component: 'InvitationsSettings' },
          'Failed to accept invitation',
          error instanceof Error ? error : new Error(String(error)),
        );
        addToast({
          description: `Failed to accept invitation to ${orgName}. Please try again.`,
          color: 'danger',
        });
      }
    }
  };

  const handleDecline = async (invitationId: string, orgName: string): Promise<void> => {
    if (
      !window.confirm(
        `Are you sure you want to decline the invitation to join ${orgName}?\n\nYou can request a new invitation from an organization admin if needed.`,
      )
    ) {
      return;
    }

    logger.info({ component: 'InvitationsSettings' }, 'Declining invitation', {
      invitationId,
      orgName,
    });

    try {
      await declineMutation.mutateAsync(invitationId);
      logger.info({ component: 'InvitationsSettings' }, 'Invitation declined successfully');
    } catch (error) {
      logger.error(
        { component: 'InvitationsSettings' },
        'Failed to decline invitation',
        error instanceof Error ? error : new Error(String(error)),
      );
      window.alert('Failed to decline invitation. Please try again.');
    }
  };

  let content: ReactNode = null;

  if (isLoading) {
    content = (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
      </div>
    );
  } else if (!invitations || invitations.length === 0) {
    content = (
      <Feedback
        description="You'll see invitations here when someone invites you to join their organization."
        title="No pending invitations"
        type="no-results"
      />
    );
  } else if (invitations?.length > 0) {
    content = (
      <div className="space-y-4">
        {invitations.map(invitation => (
          <Card key={invitation.id} className="w-full">
            <CardHeader className="flex flex-col items-start gap-2 pb-3">
              <div className="flex justify-between items-start w-full">
                <div>
                  <h3 className="text-xl font-semibold">{invitation.organization_name}</h3>
                  <p className="text-sm text-default-500 mt-1">
                    Invited{' '}
                    {new Date(invitation.invited_at).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                  {invitation.source === 'domain_governance' ? (
                    <p className="text-sm text-default-500 mt-0.5">
                      Your email domain {invitation.domain ? `@${invitation.domain}` : ''} is
                      managed by this organization
                    </p>
                  ) : invitation.inviter_name || invitation.inviter_email ? (
                    <p className="text-sm text-default-500 mt-0.5">
                      Invited by {invitation.inviter_name ?? invitation.inviter_email}
                      {invitation.inviter_name && invitation.inviter_email && (
                        <> ({invitation.inviter_email})</>
                      )}
                    </p>
                  ) : null}
                </div>
                <Chip color="primary" size="sm">
                  {invitation.role}
                </Chip>
              </div>
            </CardHeader>
            <CardBody className="pt-0">
              <div className="flex gap-2 justify-end">
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  startContent={<XCircleIcon className="h-4 w-4" />}
                  onPress={() => void handleDecline(invitation.id, invitation.organization_name)}
                  isLoading={declineMutation.isPending}
                >
                  Decline
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  startContent={<CheckCircleIcon className="h-4 w-4" />}
                  onPress={() => void handleAccept(invitation.id, invitation.organization_name)}
                  isLoading={acceptMutation.isPending}
                >
                  Accept Invitation
                </Button>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">Pending Invitations</h1>
      <p className="text-muted mb-4">
        Manage your organization invitations. Accept to join or decline to remove.
      </p>

      {content}

      <div className="mt-8 p-4 bg-surface rounded-lg">
        <h3 className="font-semibold mb-2">📧 Email Verification</h3>
        <p className="text-sm text-default-600">
          You can accept invitations directly here. For added security in production environments,
          we also send an email with an acceptance link that verifies email ownership.
        </p>
      </div>
    </div>
  );
}
